import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { calculateLevel, getLevelTitle, getXPForNextLevel, calculateXPReward } from '../utils/xpSystem';
import { checkAchievements } from '../utils/achievementChecker';

const GameContext = createContext(null);

const STORAGE_KEY = 'commandlearn_game_state';
const THEME_KEY = 'commandlearn-theme';

const initialState = {
  totalXP: 0,
  currentStreak: 0,
  bestStreak: 0,
  achievements: [],
  completedLessons: {},
  lessonsWithoutHints: 0,
  fastAnswers: 0,
  bossesDefeated: 0,
  dailyStreak: 0,
  lastPlayedDate: null,
  dailyChallengeCompleted: false,
  topicProgress: {}
};

export const GameProvider = ({ children }) => {
  const [gameState, setGameState] = useState(() => {
    // Load from localStorage on initial render
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...initialState, ...parsed };
      }
    } catch (error) {
      console.error('Failed to load game state:', error);
    }
    return initialState;
  });

  const [pendingAchievements, setPendingAchievements] = useState([]);
  const [showXPGain, setShowXPGain] = useState(null);

  // Theme state
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem(THEME_KEY);
      return saved || 'light';
    } catch (error) {
      return 'light';
    }
  });

  // Apply theme to document and persist
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch (error) {
      console.error('Failed to save theme:', error);
    }
  }, [theme]);

  // Toggle theme function
  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  }, []);

  // Persist to localStorage whenever state changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(gameState));
    } catch (error) {
      console.error('Failed to save game state:', error);
    }
  }, [gameState]);

  // Check for daily reset
  useEffect(() => {
    const today = new Date().toDateString();
    if (gameState.lastPlayedDate && gameState.lastPlayedDate !== today) {
      // New day - reset daily challenge
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const wasYesterday = gameState.lastPlayedDate === yesterday.toDateString();

      setGameState(prev => ({
        ...prev,
        dailyChallengeCompleted: false,
        dailyStreak: wasYesterday ? prev.dailyStreak : 0,
        lastPlayedDate: today
      }));
    } else if (!gameState.lastPlayedDate) {
      setGameState(prev => ({
        ...prev,
        lastPlayedDate: today
      }));
    }
  }, [gameState.lastPlayedDate]);

  // Computed values
  const level = calculateLevel(gameState.totalXP);
  const levelTitle = getLevelTitle(level);
  const xpForNextLevel = getXPForNextLevel(gameState.totalXP);
  const currentLevelXP = gameState.totalXP - (level > 1 ? [0, 0, 100, 300, 600, 1000, 1500, 2200, 3000][level] : 0);

  // Award XP for completing a lesson
  const awardXP = useCallback((difficulty, options = {}) => {
    const {
      usedHint = false,
      usedAnswer = false,
      timeInSeconds = null,
      lessonId = null,
      topic = null
    } = options;

    const xpGained = calculateXPReward(difficulty, gameState.currentStreak, {
      usedHint,
      usedAnswer,
      timeInSeconds
    });

    setGameState(prev => {
      const newState = {
        ...prev,
        totalXP: prev.totalXP + xpGained,
        currentStreak: prev.currentStreak + 1,
        bestStreak: Math.max(prev.bestStreak, prev.currentStreak + 1),
        lessonsWithoutHints: (!usedHint && !usedAnswer) ? prev.lessonsWithoutHints + 1 : prev.lessonsWithoutHints,
        fastAnswers: (timeInSeconds && timeInSeconds < 5) ? prev.fastAnswers + 1 : prev.fastAnswers
      };

      // Track completed lessons by topic
      if (lessonId && topic) {
        const topicLessons = prev.completedLessons[topic] || [];
        if (!topicLessons.includes(lessonId)) {
          newState.completedLessons = {
            ...prev.completedLessons,
            [topic]: [...topicLessons, lessonId]
          };
        }
      }

      // Check for new achievements
      const newAchievements = checkAchievements(newState, prev.achievements);
      if (newAchievements.length > 0) {
        newState.achievements = [...prev.achievements, ...newAchievements.map(a => a.id)];
        setPendingAchievements(current => [...current, ...newAchievements]);
      }

      return newState;
    });

    // Show XP gain animation
    setShowXPGain(xpGained);
    setTimeout(() => setShowXPGain(null), 2000);

    return xpGained;
  }, [gameState.currentStreak]);

  // Reset streak on wrong answer
  const resetStreak = useCallback(() => {
    setGameState(prev => ({
      ...prev,
      currentStreak: 0
    }));
  }, []);

  // Complete a boss battle
  const completeBoss = useCallback(() => {
    setGameState(prev => {
      const newState = {
        ...prev,
        bossesDefeated: prev.bossesDefeated + 1
      };

      // Check for boss-related achievements
      const newAchievements = checkAchievements(newState, prev.achievements);
      if (newAchievements.length > 0) {
        newState.achievements = [...prev.achievements, ...newAchievements.map(a => a.id)];
        setPendingAchievements(current => [...current, ...newAchievements]);
      }

      return newState;
    });
  }, []);

  // Complete daily challenge
  const completeDailyChallenge = useCallback(() => {
    setGameState(prev => {
      const newState = {
        ...prev,
        dailyChallengeCompleted: true,
        dailyStreak: prev.dailyStreak + 1
      };

      // Check for daily-related achievements
      const newAchievements = checkAchievements(newState, prev.achievements);
      if (newAchievements.length > 0) {
        newState.achievements = [...prev.achievements, ...newAchievements.map(a => a.id)];
        setPendingAchievements(current => [...current, ...newAchievements]);
      }

      return newState;
    });
  }, []);

  // Clear a pending achievement (after showing popup)
  const clearPendingAchievement = useCallback(() => {
    setPendingAchievements(current => current.slice(1));
  }, []);

  // Get topic progress percentage
  const getTopicProgress = useCallback((topic, totalLessons) => {
    const completed = gameState.completedLessons[topic]?.length || 0;
    return Math.round((completed / totalLessons) * 100);
  }, [gameState.completedLessons]);

  // Reset all progress (for testing/debugging)
  const resetProgress = useCallback(() => {
    setGameState(initialState);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const value = {
    // State
    ...gameState,
    level,
    levelTitle,
    xpForNextLevel,
    currentLevelXP,
    pendingAchievements,
    showXPGain,
    theme,

    // Actions
    awardXP,
    resetStreak,
    completeBoss,
    completeDailyChallenge,
    clearPendingAchievement,
    getTopicProgress,
    resetProgress,
    toggleTheme
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

export default GameContext;
